import React, { useState, useEffect, useMemo } from 'react';
import { Layers, Building2, BookOpen, Edit2, ChevronDown, ChevronUp, UserPlus, Plus, ChevronRight, ArrowLeft, Search, X, Activity, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import { unescapeHTML } from '../../utils/sanitize';
import { useAuth } from '../../context/AuthContext';

const ManageBatches = () => {
  const { user } = useAuth();
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCollegeId, setSelectedCollegeId] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const navigate = useNavigate();

  // Filter and Sort states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [sortBy, setSortBy] = useState('');

  const toggleGroup = (groupIdx) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupIdx]: !prev[groupIdx]
    }));
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setStateFilter('');
    setCityFilter('');
    setBatchFilter('');
    setSortBy('');
  };

  // Check if status data is available in the fetched colleges
  const hasStatusData = useMemo(() => colleges.some(c => 'status' in c), [colleges]);

  // Extract unique States from existing data
  const states = useMemo(() => {
    const uniqueStates = new Set();
    colleges.forEach(c => {
      if (c.address?.state) {
        uniqueStates.add(c.address.state);
      }
    });
    return Array.from(uniqueStates).sort();
  }, [colleges]);

  // Extract unique Cities from existing data (filtered by selected State)
  const cities = useMemo(() => {
    const uniqueCities = new Set();
    colleges.forEach(c => {
      if (c.address?.city && (!stateFilter || c.address.state === stateFilter)) {
        uniqueCities.add(c.address.city);
      }
    });
    return Array.from(uniqueCities).sort();
  }, [colleges, stateFilter]);

  // Check if updatedAt or createdAt is available in the fetched colleges
  const hasRecentlyUpdated = useMemo(() => colleges.some(c => c.updatedAt || c.createdAt), [colleges]);

  // Filter and sort logic
  const filteredAndSortedColleges = useMemo(() => {
    let result = [...colleges];

    // 1. Search filter: Name or Code (case-insensitive, partial matching)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c => 
        (c.collegeName && c.collegeName.toLowerCase().includes(term)) ||
        (c.collegeCode && c.collegeCode.toLowerCase().includes(term))
      );
    }

    // 2. Status filter
    if (statusFilter) {
      result = result.filter(c => c.status?.toLowerCase() === statusFilter.toLowerCase());
    }

    // 3. State filter
    if (stateFilter) {
      result = result.filter(c => c.address?.state === stateFilter);
    }

    // 4. City filter
    if (cityFilter) {
      result = result.filter(c => c.address?.city === cityFilter);
    }

    // 5. Batch status filter
    if (batchFilter) {
      result = result.filter(c => {
        const depts = c.departments || [];
        if (batchFilter === 'has_active') {
          return depts.some(d => (d.batches || []).some(b => b.status?.toLowerCase() === 'active'));
        } else if (batchFilter === 'no_active') {
          return depts.length > 0 && !depts.some(d => (d.batches || []).some(b => b.status?.toLowerCase() === 'active'));
        } else if (batchFilter === 'no_batches') {
          return depts.length === 0 || !depts.some(d => d.batches && d.batches.length > 0);
        }
        return true;
      });
    }

    // 6. Sorting
    if (sortBy) {
      result.sort((a, b) => {
        if (sortBy === 'name_asc') {
          return (a.collegeName || '').localeCompare(b.collegeName || '');
        }
        if (sortBy === 'name_desc') {
          return (b.collegeName || '').localeCompare(a.collegeName || '');
        }
        if (sortBy === 'recently_updated') {
          const dateA = new Date(a.updatedAt || a.createdAt || 0);
          const dateB = new Date(b.updatedAt || b.createdAt || 0);
          return dateB - dateA;
        }
        if (sortBy === 'highest_batches') {
          return (b.departments?.length || 0) - (a.departments?.length || 0);
        }
        if (sortBy === 'lowest_batches') {
          return (a.departments?.length || 0) - (b.departments?.length || 0);
        }
        return 0;
      });
    }

    return result;
  }, [colleges, searchTerm, statusFilter, stateFilter, cityFilter, batchFilter, sortBy]);

  useEffect(() => {
    fetchColleges();
  }, []);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/colleges', { params: { limit: 9999 } });
      if (data.success) {
        setColleges(data.data);
        // Auto-select college for college_admin
        if (data.data.length === 1 && user?.role === 'college_admin') {
          setSelectedCollegeId(data.data[0]._id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch colleges:', error);
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const selectedCollege = colleges.find(c => c._id === selectedCollegeId);

  const getGroupedDepartments = (departments) => {
    if (!departments || departments.length === 0) return [];
    return departments.map(dept => {
      const degreeAbbr = unescapeHTML(dept.abbreviation || dept.fullName || 'Degree');
      const specStr = Array.isArray(dept.specialization)
        ? dept.specialization.map(s => unescapeHTML(s)).filter(s => s && s !== 'General').join(', ')
        : (dept.specialization && dept.specialization !== 'General' ? unescapeHTML(dept.specialization) : '');
      const spec = specStr ? ` in ${specStr}` : '';
      const friendlyName = `${degreeAbbr}${spec}`;
      
      return {
        referenceName: friendlyName,
        degree: friendlyName,
        batches: dept.batches || []
      };
    });
  };


  return (
    <div className="min-h-screen bg-gray-50/50 dark:bg-[#00152E]/5 p-4 lg:p-6">
      {/* Header */}
      {!selectedCollegeId && (
      <div className="bg-white dark:bg-[#002147] rounded-2xl shadow-sm border border-gray-200 dark:border-[#1a3884]/35 p-5 mb-6 animate-fade-in">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-blue-400 hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884] dark:hover:border-[#1a3884] dark:hover:text-white rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 group shadow-sm flex items-center justify-center shrink-0"
              title="Go Back"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <div className="p-2 bg-[#1a3884]/10 dark:bg-[#1a3884]/20 rounded-xl shrink-0">
              <Layers className="w-6 h-6 text-[#1a3884] dark:text-[#4f7dff]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#1a3884] dark:text-[#4f7dff] mb-1">
                Manage Batches
              </h1>
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <Activity className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
                <span className="font-medium">Select an institution to view and manage its batches</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="border-t border-gray-100 dark:border-[#1a3884]/20 mt-5 pt-5">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 w-full">
            {/* Search Field */}
            <div className="flex-grow lg:flex-[2]">
              <Input
                placeholder="Search Institution..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={Search}
              />
            </div>
            
            {/* Dropdowns and Clear Button */}
            <div className="flex flex-wrap md:flex-nowrap items-center gap-3 flex-grow lg:flex-[3]">
              {hasStatusData && (
                <Select
                  placeholder="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  options={[
                    { value: 'Active', label: 'Active' },
                    { value: 'Inactive', label: 'Inactive' }
                  ]}
                  className="w-full md:w-auto md:flex-1"
                />
              )}
              
              {states.length > 0 && (
                <>
                  <Select
                    placeholder="State"
                    value={stateFilter}
                    onChange={(e) => {
                      setStateFilter(e.target.value);
                      setCityFilter('');
                    }}
                    options={states.map(s => ({ value: s, label: s }))}
                    className="w-full md:w-auto md:flex-1"
                  />
                  <Select
                    placeholder="City"
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    options={cities.map(c => ({ value: c, label: c }))}
                    disabled={!stateFilter}
                    className="w-full md:w-auto md:flex-1"
                  />
                </>
              )}

              <Select
                placeholder="Batch Status"
                value={batchFilter}
                onChange={(e) => setBatchFilter(e.target.value)}
                options={[
                  { value: 'has_active', label: 'Has Active Batches' },
                  { value: 'no_active', label: 'No Active Batches' },
                  { value: 'no_batches', label: 'No Batches Created' }
                ]}
                className="w-full md:w-auto md:flex-1"
              />

              <Select
                placeholder="Sort By"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                options={[
                  { value: 'name_asc', label: 'Institution Name (A–Z)' },
                  { value: 'name_desc', label: 'Institution Name (Z–A)' },
                  ...(hasRecentlyUpdated ? [{ value: 'recently_updated', label: 'Recently Updated' }] : []),
                  { value: 'highest_batches', label: 'Highest Batch Count' },
                  { value: 'lowest_batches', label: 'Lowest Batch Count' }
                ]}
                className="w-full md:w-auto md:flex-1"
              />

              {(searchTerm || statusFilter || stateFilter || cityFilter || batchFilter || sortBy) && (
                <button
                  onClick={handleClearFilters}
                  className="py-2 px-4 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition-colors border border-red-200 dark:border-red-800/40 w-full md:w-auto whitespace-nowrap flex items-center justify-center gap-1.5"
                >
                  <X className="w-4 h-4" /> Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* College Content */}
      <div className="space-y-6">
        {loading ? (
          <div className="flex justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a3884]"></div>
          </div>
        ) : !selectedCollegeId ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
            {filteredAndSortedColleges.map((college) => (
              <div 
                key={college._id} 
                className="bg-white dark:bg-[#002147] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:border-[#1a3884]/40 dark:hover:border-[#1a3884]/50 transition-all group cursor-pointer"
                onClick={() => setSelectedCollegeId(college._id)}
              >
                <div className="p-6 flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#1a3884]/10 dark:bg-[#1a3884]/20 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 border border-[#1a3884]/20 dark:border-[#1a3884]/40">
                    {college.logo ? (
                      <img src={college.logo.startsWith('http') ? college.logo : `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${college.logo}`} alt="Logo" className="w-full h-full object-cover rounded-xl" />
                    ) : (
                      <Building2 className="w-7 h-7 text-[#1a3884] dark:text-[#4f7dff]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 pt-1">
                    <h3 className="font-bold text-gray-900 dark:text-white truncate" title={college.collegeName}>
                      {college.collegeName}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-1.5">
                      <span className="font-semibold bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded text-xs">{college.collegeCode}</span>
                      <span className="w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></span>
                      <span className="text-xs font-medium">{college.departments?.length || 0} Degrees</span>
                    </p>
                  </div>
                </div>
                <div className="px-6 py-3 bg-gray-50/80 dark:bg-[#001f40]/50 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center">
                  <span className="text-xs text-gray-400 font-medium">Click to view batches</span>
                  <button 
                    className="text-sm font-bold text-[#1a3884] dark:text-[#4f7dff] group-hover:text-[#132A55] dark:group-hover:text-white transition-colors flex items-center gap-1"
                  >
                    Manage Batches <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {colleges.length === 0 && (
              <div className="col-span-full text-center py-16 bg-white dark:bg-[#002147] rounded-xl border border-gray-100 dark:border-gray-800">
                <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">No Institutions Found</h3>
                <p className="text-gray-500 dark:text-gray-400 mt-1">There are no institutions onboarded yet.</p>
              </div>
            )}
            {colleges.length > 0 && filteredAndSortedColleges.length === 0 && (
              <div className="col-span-full text-center py-16 bg-white dark:bg-[#002147] rounded-xl border border-gray-100 dark:border-gray-800">
                <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">No Institutions Found</h3>
                <p className="text-gray-500 dark:text-gray-400 mt-1">No institutions match your search or filter criteria. Try changing or clearing your filters.</p>
                <button
                  onClick={handleClearFilters}
                  className="mt-4 px-4 py-2 bg-[#1a3884]/10 dark:bg-[#1a3884]/20 text-[#1a3884] dark:text-[#4f7dff] font-medium rounded-lg hover:bg-[#1a3884]/20 dark:hover:bg-[#1a3884]/30 transition-colors inline-flex items-center gap-2"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        ) : selectedCollege && (!selectedCollege.departments || selectedCollege.departments.length === 0) ? (
          <div className="bg-white dark:bg-[#002147] rounded-xl p-12 text-center border border-gray-100 dark:border-gray-800">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">No Batches Found</h3>
            <p className="text-gray-500 dark:text-gray-400 mt-1">This institution currently does not have any degrees or batches configured.</p>
            <button
              onClick={() => navigate(`/colleges/edit/${selectedCollege._id}?degreesOnly=true`)}
              className="mt-4 px-4 py-2 bg-[#1a3884]/10 dark:bg-[#1a3884]/20 text-[#1a3884] dark:text-[#4f7dff] font-medium rounded-lg hover:bg-[#1a3884]/20 dark:hover:bg-[#1a3884]/30 transition-colors inline-flex items-center gap-2"
            >
              <Edit2 size={16} /> Add Degrees & Batches
            </button>
          </div>
        ) : selectedCollege ? (
          <div className="bg-white dark:bg-[#002147] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-sm animate-fade-in">
            {/* College Header */}
            <div className="bg-gray-50/80 dark:bg-[#001f40]/50 px-6 py-5 border-b border-gray-200 dark:border-gray-800 flex flex-col lg:flex-row justify-between lg:items-center gap-4">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                {user?.role !== 'college_admin' && (
                <button
                  onClick={() => setSelectedCollegeId('')}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-xl text-gray-500 transition-colors flex-shrink-0"
                  title="Back to Institutions"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                )}
                <div className="w-12 h-12 bg-[#1a3884]/10 dark:bg-[#1a3884]/20 rounded-xl flex items-center justify-center shadow-sm">
                  {selectedCollege.logo ? (
                    <img src={selectedCollege.logo.startsWith('http') ? selectedCollege.logo : `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${selectedCollege.logo}`} alt="Logo" className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    <Building2 className="w-6 h-6 text-[#1a3884] dark:text-[#4f7dff]" />
                  )}
                </div>
                <div className="min-w-0">
                  <h2 className="font-bold text-gray-900 dark:text-white text-lg leading-snug truncate" title={selectedCollege.collegeName}>{selectedCollege.collegeName}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-bold bg-white dark:bg-gray-800 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700">
                      {selectedCollege.collegeCode}
                    </span>
                    <span className="text-xs text-[#1a3884] dark:text-[#4f7dff] font-bold bg-[#1a3884]/10 dark:bg-[#1a3884]/20 px-2 py-0.5 rounded border border-[#1a3884]/20 dark:border-[#1a3884]/40">
                      {selectedCollege.institutionType}
                    </span>
                  </div>
                </div>
              </div>
              {/* One aligned action row — compact buttons so all three sit on a
                  single line beside the title on desktop. */}
              <div className="flex flex-wrap items-center lg:justify-end gap-2 shrink-0">
                <button
                  onClick={() => navigate(`/admin/student-onboarding/add?collegeId=${selectedCollegeId}`)}
                  className="px-3.5 py-2 bg-[#1a3884] hover:bg-[#132A55] text-white text-[12.5px] font-semibold rounded-lg transition-all flex items-center gap-1.5 shadow-sm"
                  title="Onboard Student"
                >
                  <UserPlus size={14} /> Onboard Student
                </button>
                <button
                  onClick={() => navigate(`/colleges/edit/${selectedCollege._id}?degreesOnly=true`)}
                  className="px-3.5 py-2 bg-[#1a3884]/5 hover:bg-[#1a3884]/10 dark:bg-[#4f7dff]/10 dark:hover:bg-[#4f7dff]/20 text-[#1a3884] dark:text-[#4f7dff] text-[12.5px] font-semibold rounded-lg border border-[#1a3884]/15 dark:border-[#4f7dff]/25 transition-colors flex items-center gap-1.5"
                  title="Add Degree"
                >
                  <Plus size={14} /> Add Degree
                </button>
                <button
                  onClick={() => navigate(`/colleges/edit/${selectedCollege._id}`)}
                  className="px-3.5 py-2 bg-[#1a3884]/5 hover:bg-[#1a3884]/10 dark:bg-[#4f7dff]/10 dark:hover:bg-[#4f7dff]/20 text-[#1a3884] dark:text-[#4f7dff] text-[12.5px] font-semibold rounded-lg border border-[#1a3884]/15 dark:border-[#4f7dff]/25 transition-colors flex items-center gap-1.5"
                  title="Manage Institution"
                >
                  <Edit2 size={14} /> Manage Institution
                </button>
              </div>
            </div>

            {/* Batches Content */}
            <div className="p-6">
              <div className="flex flex-col gap-3">
                {getGroupedDepartments(selectedCollege.departments).map((group, groupIdx) => (
                  <div
                    key={groupIdx}
                    className="flex flex-col bg-white dark:bg-[#001E3D]/50 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm transition-all overflow-hidden"
                  >
                    <div 
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#001736]/50 transition-colors"
                      onClick={() => toggleGroup(groupIdx)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2.5 bg-[#1a3884]/10 dark:bg-[#1a3884]/20 text-[#1a3884] dark:text-[#4f7dff] rounded-lg flex-shrink-0">
                          <BookOpen size={20} />
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 min-w-0">
                          <h4 className="text-[15px] font-bold text-gray-900 dark:text-white tracking-tight truncate">
                            {group.referenceName}
                          </h4>
                          {/* Only show the degree chip when it actually adds
                              information — repeating the title reads as noise. */}
                          {group.degree && group.degree !== group.referenceName && (
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 text-[#1a3884] dark:text-[#4f7dff] border border-[#1a3884]/15 dark:border-[#4f7dff]/25 w-max">
                              {group.degree}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-[11px] font-bold text-[#1a3884] dark:text-[#4f7dff] bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 border border-[#1a3884]/15 dark:border-[#4f7dff]/25 px-2.5 py-1 rounded-md">
                          {group.batches.length} Batch{group.batches.length !== 1 ? 'es' : ''}
                        </span>
                        {expandedGroups[groupIdx] ? (
                          <ChevronUp size={20} className="text-gray-400" />
                        ) : (
                          <ChevronDown size={20} className="text-gray-400" />
                        )}
                      </div>
                    </div>

                    {/* Batches List - Accordion Content */}
                    {expandedGroups[groupIdx] && (
                      <div className="p-4 pt-0 bg-gray-50/30 dark:bg-[#001736]/30 border-t border-gray-100 dark:border-gray-800">
                        <div className="flex flex-col gap-2 w-full mt-3">
                          {group.batches.map((batch, bIdx) => (
                            <div key={bIdx} className="flex flex-wrap items-center gap-2 md:gap-3 bg-white dark:bg-[#001f40] p-3 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:border-[#1a3884]/40 dark:hover:border-[#1a3884]/50 transition-colors">
                              <span className="text-[13px] font-bold text-gray-900 dark:text-white w-32">
                                Batch: {batch.batch}
                              </span>
                              <span className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-[0.08em] w-32">
                                {batch.startYear && batch.endYear && !isNaN(parseInt(batch.startYear)) && !isNaN(parseInt(batch.endYear)) && parseInt(batch.endYear) > parseInt(batch.startYear)
                                  ? `${parseInt(batch.endYear) - parseInt(batch.startYear)} Year${parseInt(batch.endYear) - parseInt(batch.startYear) !== 1 ? 's' : ''} Program`
                                  : `${batch.startYear} - ${batch.endYear}`}
                              </span>
                              {(() => {
                                const addons = batch.subscriptionPlan?.addons || {};
                                const activeAddonsList = [];
                                if (batch.subscriptionPlan?.plan === 'Smaart Core' && addons.sq) activeAddonsList.push('SQ');
                                if ((batch.subscriptionPlan?.plan === 'Smaart Core' || batch.subscriptionPlan?.plan === 'Smaart Standard') && addons.piq) activeAddonsList.push('PIQ');
                                if (addons.britishCouncil) activeAddonsList.push('British Council');
                                if (addons.collegePlacement) activeAddonsList.push('Placement');

                                return (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 text-[#1a3884] dark:text-[#4f7dff] border border-[#1a3884]/15 dark:border-[#4f7dff]/25 uppercase tracking-wider">
                                    {batch.subscriptionPlan?.plan || 'Smaart Core'}
                                    {activeAddonsList.length > 0 && (
                                      <span className="text-[9px] font-semibold lowercase opacity-80">
                                        (+{activeAddonsList.join(', ')})
                                      </span>
                                    )}
                                  </span>
                                );
                              })()}
                              <span className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-bold border uppercase tracking-wider ${batch.status === 'Active' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800/50' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'}`}>
                                {batch.status || 'Draft'}
                              </span>
                              <div className="ml-auto flex items-center gap-2">
                                <button
                                  onClick={() => navigate(`/admin/student-onboarding/add?collegeId=${selectedCollege._id}&degreeName=${encodeURIComponent(group.degree)}&batch=${encodeURIComponent(batch.batch)}`)}
                                  className="px-2.5 py-1.5 text-[11px] font-bold text-[#1a3884] dark:text-[#4f7dff] bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 hover:bg-[#1a3884]/10 dark:hover:bg-[#4f7dff]/20 border border-[#1a3884]/15 dark:border-[#4f7dff]/25 rounded-lg transition-colors flex items-center gap-1.5"
                                  title="Add Student to this Batch"
                                >
                                  <UserPlus size={13} /> Student
                                </button>
                                <button
                                  onClick={() => navigate(`/admin/student-onboarding/add?collegeId=${selectedCollege._id}&degreeName=${encodeURIComponent(group.degree)}&batch=${encodeURIComponent(batch.batch)}&tab=bulk`)}
                                  className="px-2.5 py-1.5 text-[11px] font-bold text-[#1a3884] dark:text-[#4f7dff] bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 hover:bg-[#1a3884]/10 dark:hover:bg-[#4f7dff]/20 border border-[#1a3884]/15 dark:border-[#4f7dff]/25 rounded-lg transition-colors flex items-center gap-1.5"
                                  title="Bulk Upload Students to this Batch"
                                >
                                  <Upload size={13} /> Bulk Upload
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ManageBatches;
